<template>
    <div
        ref="plotlyChart"
        class="plotly-chart"
        style="width: 100%; height: 100%"
    ></div>
</template>

<script setup lang="ts">
import {
    nextTick,
    onMounted,
    watch,
    onUnmounted,
    useTemplateRef,
    ref,
} from "vue"
import Plotly, { ModeBarDefaultButtons, PlotData } from "plotly.js-dist-min"
// @ts-expect-error - plotly.js-locales n'est pas typé
import locale from "plotly.js-locales/fr"
import { createWorker } from "@modules/data/workers/workerLoader"
Plotly.register(locale)
Plotly.setPlotConfig({ locale: "fr" })

/** Layout Plotly libre : barres empilées, courbes, seuils, etc. */
type Graph = {
    data: object[]
    layout: Record<string, unknown>
    /** Réservé ; `renderChart` applique une config locale (locale, mode bar…). */
    config?: object
}

type Props = {
    /**
     * Objet contenant les données du graphique.
     * - `data` Tableau d'objets qui contient les données à afficher.
     * - `layout` Objet qui configure l'apparence et la disposition du graphique.
     * - `config` Objet pour configurer les options d'interaction avec le graphique.
     */
    graph: Graph | null
    enableExport?: boolean
}

const emit = defineEmits(["loaded", "click:export"])

const props = withDefaults(defineProps<Props>(), {
    enableExport: false,
})
const plotlyChart = useTemplateRef("plotlyChart")

/** `true` après le premier `Plotly.newPlot` — les mises à jour suivantes utilisent `animate` / `react`. */
const isPlotInitialized = ref(false)

/** Référence Plotly renvoyée par `newPlot` (typage correct pour `animate` / `react`). */
const plotlyGraphDiv = ref<Plotly.PlotlyHTMLElement | null>(null)

/** Observer la taille du conteneur pour recaler Plotly (évite le débordement hors cadre flex). */
let plotResizeObserver: ResizeObserver | null = null

const worker = createWorker()

/** Durée et easing des transitions entre jeux de données (scatter, mêmes traces). */
const DATA_UPDATE_ANIMATION_MS = 500
const DATA_UPDATE_EASING = "cubic-in-out"

type RangeData = { min: number; max: number | null }

// Recevoir le résultat du Web Worker
worker.onmessage = function (e: {
    data: {
        y1y2: RangeData
        y3: RangeData | null
    }
}) {
    function handleMinMaxLayout(data: RangeData, axis: string[]) {
        if (!plotlyChart.value) return

        // Si les données sont nulles, ne rien faire
        if (data.max === null) return

        const relayout = {} as Record<string, any>

        const newYRange = [data.min < 0 ? data.min : 0, data.max] as [
            number,
            number,
        ]

        for (const ax of axis) {
            if (props.graph?.layout?.[ax as keyof typeof props.graph.layout]) {
                const layout = props.graph.layout as Record<string, any>

                const axisLayout = layout[ax]
                if (!axisLayout) continue

                relayout[ax] = {
                    ...axisLayout,
                    range: newYRange,
                }
            }
        }

        Plotly.relayout(plotlyChart.value, relayout)
    }

    handleMinMaxLayout(e.data.y1y2, ["yaxis", "yaxis2"])

    if (e.data.y3) {
        handleMinMaxLayout(e.data.y3, ["yaxis3"])
    }
}

// Gérer les erreurs du Web Worker (facultatif)
worker.onerror = function (error) {
    console.error("Erreur dans le Web Worker", error)
    worker.terminate()
}

/**
 * Config Plotly locale (mode bar, export, locale) — stable pour `react` / même instance.
 */
function buildPlotConfig(): Record<string, unknown> {
    const config = {
        responsive: true,
        displaylogo: false,
        locale: "fr",
        modeBarButtonsToRemove: [
            "pan2d",
            "zoomOut2d",
            "zoomIn2d",
            "zoom2d",
            "lasso2d",
            "select2d",
            "resetScale2d",
        ] as ModeBarDefaultButtons[],
    } as Record<string, unknown>
    if (props.enableExport) {
        ;(config as Record<string, unknown>).modeBarButtonsToAdd = [
            {
                name: "exportdata",
                title: "Exporter les données",
                icon: Plotly.Icons.disk,
                click: () => {
                    emit("click:export")
                },
            },
        ]
    }
    return config
}

/**
 * Met à jour données + layout sans recréer le nœud DOM (`Plotly.animate`).
 * Ne pas enchaîner avec `xaxis.autorange` après l’animation : cela provoquait un
 * second recalcul de l’axe des abscisses et un saut visuel (ex. carte comparateur EM).
 * Le `layout` fourni par l’API contient déjà les plages / `autorange` nécessaires.
 * Repli : `Plotly.react` si l’animation échoue.
 */
async function updatePlotDataInPlace(
    gd: Plotly.PlotlyHTMLElement,
    data: object[],
    layout: Record<string, unknown>,
    config: Record<string, unknown>
): Promise<void> {
    const traces = data.map((_, i) => i)
    try {
        await Plotly.animate(
            gd,
            {
                data: data as Plotly.Data[],
                layout: layout as Partial<Plotly.Layout>,
                traces,
            },
            {
                transition: {
                    duration: DATA_UPDATE_ANIMATION_MS,
                    easing: DATA_UPDATE_EASING,
                },
                frame: {
                    duration: DATA_UPDATE_ANIMATION_MS,
                    redraw: true,
                },
            }
        )
    } catch {
        await Plotly.react(gd, data, layout, config)
    }
}

/**
 * Écoute `plotly_restyle` une seule fois (calcul min/max worker).
 */
function onPlotlyRestyle(data: unknown, gd: Plotly.PlotlyHTMLElement) {
    sendWorkerRestyleEvent(data, gd)
}

/**
 * Recalcule la taille du tracé pour la boîte DOM actuelle (`responsive` + conteneur en flex).
 */
function plotlyResizeToContainer(): void {
    const el = plotlyChart.value
    if (!el || !isPlotInitialized.value) return
    try {
        Plotly.Plots.resize(el)
    } catch {
        /* no-op */
    }
}

/**
 * Écoute les changements de taille du nœud racine du graphique.
 */
function setupPlotResizeObserver(): void {
    const el = plotlyChart.value
    if (!el || typeof ResizeObserver === "undefined") return
    plotResizeObserver?.disconnect()
    plotResizeObserver = new ResizeObserver(() => {
        plotlyResizeToContainer()
    })
    plotResizeObserver.observe(el)
}

/**
 * Render le graphique Plotly en utilisant les données fournis dans graph
 * et ajoute un événement pour redimensionner le graphique lors du redimensionnement
 * de la fenêtre
 * @returns {void}
 */
const renderChart = async (): Promise<void> => {
    if (!props.graph) return

    const { data, layout } = props.graph
    const config = buildPlotConfig()

    if (!plotlyChart.value) return

    if (!isPlotInitialized.value) {
        const plotlyLoaded = await Plotly.newPlot(
            plotlyChart.value,
            data,
            layout,
            config
        )
        plotlyGraphDiv.value = plotlyLoaded
        isPlotInitialized.value = true

        plotlyLoaded.on("plotly_restyle", (ev: unknown) => {
            onPlotlyRestyle(ev, plotlyLoaded)
        })

        await Plotly.relayout(plotlyChart.value, {
            "xaxis.autorange": true,
        })

        emit("loaded")
    } else {
        const gd = plotlyGraphDiv.value
        if (gd) {
            await updatePlotDataInPlace(gd, data, layout, config)
        }
    }

    await nextTick()
    plotlyResizeToContainer()
}

function sendWorkerRestyleEvent(
    data: any,
    plotlyLoaded: Plotly.PlotlyHTMLElement
) {
    const result = data as [{ visible: (string | boolean)[] }, number[]]
    const indexModified = result[1]
    const traces = plotlyLoaded.data.map((trace: any, index) => {
        // si dans le tableau des traces modifiées
        const r = {
            trace: trace.name,
            visible: trace.visible,
            showlegend: trace.showlegend,
        }

        if (indexModified.includes(index)) {
            const indexOf = result[1].indexOf(index)
            r.visible = result[0].visible[indexOf]
        }
        return r
    })
    if (!plotlyLoaded || !traces) return
    // récupère les données des traces visibles
    const visibleTraces = plotlyLoaded.data
        .filter((_, index) => {
            return (
                traces[index].visible &&
                traces[index].visible !== "legendonly" &&
                traces[index].showlegend
            )
        })

        .map((trace) => {
            // Forme le type de données attendu par le Web Worker
            const t = trace as PlotData
            return {
                type: t.type,
                data: t.y,
                yaxis: t.yaxis,
            }
        })
    // Envoyer les données au Web Worker (les traces visibles et la longueur des données)
    worker.postMessage({
        data: JSON.stringify(visibleTraces),
        barmode: (props.graph?.layout as any)?.barmode ?? "group",
    })
}

/**
 * Redimmensionne le graphique pour afficher l'axe des abscisses en entier
 */
function autoScale() {
    if (plotlyChart.value) {
        Plotly.relayout(plotlyChart.value, {
            "xaxis.autorange": true,
        })
    }
}

/**
 * Render le graphique lorsque le composant est monté
 */
onMounted(() => {
    void (async () => {
        await renderChart()
        autoScale()
        await nextTick()
        plotlyResizeToContainer()
        setupPlotResizeObserver()
    })()
})

onUnmounted(() => {
    plotResizeObserver?.disconnect()
    plotResizeObserver = null
    worker.terminate()
    if (plotlyChart.value && isPlotInitialized.value) {
        Plotly.purge(plotlyChart.value)
        isPlotInitialized.value = false
        plotlyGraphDiv.value = null
    }
})

/**
 * Observe les changements dans graph et redessine le graphique lorsque les données changent
 */
watch(
    () => props.graph,
    () => {
        void renderChart()
    }
)

defineExpose({
    autoScale: autoScale,
})
</script>

<style scoped>
/* Taille réduite pour petits écrans */
@media (max-width: 768px) {
    #plotlyChart {
        height: 300px;
    }
}
</style>
